import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Alert, Button, InputNumber, message } from 'antd';

import { selectors, createODA, updateOda, fetchBlotter } from 'state';

type Props = {
  id: number;
  onComplete: () => void;
  tradingBlocked: boolean;
};

const ODAForm: React.FC<Props> = ({ id, onComplete, tradingBlocked }) => {
  const dispatch = useDispatch();

  const { fetched_blotter, fetching_blotter } = useSelector(selectors.player);
  const financialInstrument = useSelector(
    selectors.financialInstrumentById(id),
  );
  const underlyingAsset = useSelector(
    selectors.underlyingAssetOfInstrument(financialInstrument),
  );

  const oda = useSelector(selectors.odaOfInstrumentCreateSelector(id));

  const { stopLoss, takeProfit } = oda ?? {};

  const currentPrice = useMemo(() => {
    const instrumentAmount = financialInstrument.amount;
    const currentBid = underlyingAsset.bid;
    const currentAsk = underlyingAsset.ask;
    return instrumentAmount > 0 ? currentBid : currentAsk;
  }, [underlyingAsset, financialInstrument]);

  // const [isOpen] = utils.assetIsOpen(underlyingAsset, session);

  const [stopLossPrice, setStopLossPrice] = useState<number | undefined>(
    stopLoss?.limit_price || currentPrice,
  );
  const [takeProfitPrice, setTakeProfitPrice] = useState<number | undefined>(
    takeProfit?.limit_price || currentPrice,
  );
  const [stopLossDirty, setStopLossDirty] = useState(false);
  const [takeProfitDirty, setTakeProfitDirty] = useState(false);

  // Pull orders if not already
  useEffect(() => {
    if (fetching_blotter || fetched_blotter) {
      return;
    }
    dispatch(fetchBlotter({}));
  }, [dispatch, fetching_blotter, fetched_blotter]);

  // Set new price
  useEffect(() => {
    if (!stopLossDirty) {
      setStopLossPrice(stopLoss?.limit_price || currentPrice);
    }
    if (!takeProfitDirty) {
      setTakeProfitPrice(takeProfit?.limit_price || currentPrice);
    }
  }, [currentPrice, stopLoss, stopLossDirty, takeProfit, takeProfitDirty]);

  // if (!isOpen) {
  //   return null;
  // }

  if (oda !== undefined && (!stopLoss || !takeProfit)) {
    return (
      <Alert type="info" showIcon message="Loading the existing ODA orders…" />
    );
  }

  const { name } = underlyingAsset;

  const stopTooHigh =
    financialInstrument.amount > 0 &&
    typeof stopLossPrice === 'number' &&
    stopLossPrice > currentPrice;
  const stopTooLow =
    financialInstrument.amount < 0 &&
    typeof stopLossPrice === 'number' &&
    stopLossPrice < currentPrice;
  const takeTooHigh =
    financialInstrument.amount < 0 &&
    typeof takeProfitPrice === 'number' &&
    takeProfitPrice > currentPrice;
  const takeTooLow =
    financialInstrument.amount > 0 &&
    typeof takeProfitPrice === 'number' &&
    takeProfitPrice < currentPrice;

  return (
    <div className="markets-v3-form markets-v3-oda-form">
      <div className="markets-v3-form__body">
        <div className="markets-v3-form__intro">
          Protect <strong>{name}</strong> with linked stop-loss and take-profit
          orders. Current executable price: <strong>{currentPrice}</strong>.
        </div>
        <label className="markets-v3-field-stack">
          <span>Stop loss</span>
          <InputNumber
            aria-label="Stop loss"
            value={stopLossPrice}
            onChange={val => {
              setStopLossDirty(true);
              setStopLossPrice(val);
            }}
          />
        </label>
        <label className="markets-v3-field-stack">
          <span>Take profit</span>
          <InputNumber
            aria-label="Take profit"
            value={takeProfitPrice}
            onChange={val => {
              setTakeProfitDirty(true);
              setTakeProfitPrice(Number(val || 0));
            }}
          />
        </label>
      </div>
      <div className="markets-v3-form__footer">
        <Button
          type="primary"
          block
          disabled={tradingBlocked || financialInstrument.amount === 0}
          onClick={() => {
            const fn = oda ? updateOda : createODA;

            if (stopLossPrice === undefined || takeProfitPrice === undefined) {
              message.error('Prices must be numbers!');
              return;
            }

            if (stopTooHigh) {
              message.error(
                'StopLossPrice has to be lower than current price!',
              );
              return;
            }
            if (takeTooLow) {
              message.error(
                'TakeProfitPrice has to be higher than current price!',
              );
              return;
            }

            if (stopTooLow) {
              message.error(
                'StopLossPrice has to be higher than current price!',
              );
              return;
            }
            if (takeTooHigh) {
              message.error(
                'TakeProfitPrice has to be lower than current price!',
              );
              return;
            }

            dispatch(
              fn({
                financial_instrument: financialInstrument.id,
                stop_loss_price: stopLossPrice,
                take_profit_price: takeProfitPrice,
              }),
            );
            onComplete();
          }}
        >
          {oda ? 'Update ODA' : 'Set ODA'}
        </Button>
      </div>
    </div>
  );
};

export default ODAForm;
